import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {handleActiveError} from "../../../../components/sonner/ToastBasic";
import {getCompany} from "../../../../api/company/CompanyService";
import CommonPagination from "../../../../components/table/movable/CommonPagination";
import PanelTextNav from "../../fieldMng/shared/PanelTextNav";
import CompanyManageEditor from "./CompanyManageEditor";
import CompanyRelatedInfoPanel from "./CompanyRelatedInfoPanel";
import {CommonHistoryList} from "../../history/subPages/CommonHistoryList";
import CompanyManageSidebarList from "./CompanyManageSidebarList";
import CompanyManageSearchBar from "./CompanyManageSearchBar";
import {extractObject} from "../../../../components/commonmethod/CommonMethod";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import {CacheType} from "../../../../constant/CodeSpecNum";
import JsGridExcelGridOpenButton from "../../../../components/js-grid/JsGridExcelGridOpenButton";
import "../companyManagePanel.css";
const COMPANY_TAB_OPTIONS = [
    {id: "info", label: "기본정보"},
    {id: "related", label: "연관정보"},
    {id: "history", label: "이력관리"},
];

function CompanyEditorEmpty({loading, hasSelection}) {
    if (loading) {
        return (
            <div className="code-editor-empty">
                <div className="code-editor-empty__icon">…</div>
                <p className="code-editor-empty__title">불러오는 중</p>
                <p className="code-editor-empty__desc">업체 정보를 가져오고 있습니다.</p>
            </div>
        );
    }

    return (
        <div className="code-editor-empty">
            <div className="code-editor-empty__icon">⌘</div>
            <p className="code-editor-empty__title">
                {hasSelection ? "업체를 선택하세요" : "등록된 업체가 없습니다"}
            </p>
            <p className="code-editor-empty__desc">
                {hasSelection
                    ? "왼쪽 목록에서 업체를 선택하면 이곳에서 상세 정보를 확인·수정할 수 있습니다."
                    : "검색 조건을 변경하거나 새 업체를 등록해 주세요."}
            </p>
        </div>
    );
}

function CompanyListType({data, dataHook, pageInfo}) {
    const {handleSearch, saveConditions, refresh, handlePageChange} = dataHook;
    const refreshCache = useCacheRefresh();
    const [searchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState("info");
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const selectedIdRef = useRef(null);

    const excelGridSearch = useMemo(() => extractObject(saveConditions), [saveConditions]);

    const loadCompanyDetail = useCallback(async (id) => {
        if (id == null) {
            setSelectedCompany(null);
            selectedIdRef.current = null;
            return;
        }
        selectedIdRef.current = id;
        setLoadingDetail(true);
        try {
            const response = await getCompany(id);
            if (selectedIdRef.current !== id) {
                return;
            }
            if (response?.status === 200) {
                setSelectedCompany(response.data);
            } else if (response?.response?.status === 403) {
                handleActiveError({message: "권한이 없습니다."});
            } else if (response?.response?.status === 401) {
                handleActiveError({message: "로그인이 필요합니다."});
            }
        } finally {
            if (selectedIdRef.current === id) {
                setLoadingDetail(false);
            }
        }
    }, []);

    useEffect(() => {
        const idParam = searchParams.get("selectedId");
        if (!idParam) {
            return;
        }

        const parsedId = Number(idParam);
        if (!Number.isFinite(parsedId)) {
            return;
        }

        setActiveTab("info");
        void loadCompanyDetail(parsedId);
    }, [searchParams, loadCompanyDetail]);

    useEffect(() => {
        if (!data?.length) {
            setSelectedCompany(null);
            selectedIdRef.current = null;
            return;
        }
        const currentId = selectedIdRef.current;
        const stillExists = currentId != null && data.some((item) => item.id === currentId);
        if (!stillExists) {
            void loadCompanyDetail(data[0].id);
            setActiveTab("info");
        }
    }, [data, loadCompanyDetail]);

    const handleSelectCompany = (item) => {
        if (item?.id == null || item.id === selectedIdRef.current) {
            return;
        }
        setActiveTab("info");
        void loadCompanyDetail(item.id);
    };

    const handleDeletedCompany = () => {
        selectedIdRef.current = null;
        setSelectedCompany(null);
        refreshCache(CacheType.company);
        refresh();
    };

    const selectedId = selectedCompany?.id ?? selectedIdRef.current;
    const showEditor = !loadingDetail && selectedCompany;

    return (
        <div className="code-manage-panel__body company-manage-panel">
            <div className="company-manage-panel__topbar code-manage-scope">
                <CompanyManageSearchBar
                    saveConditions={saveConditions}
                    handleSearch={handleSearch}
                />
                <div className="company-manage-panel__actions">
                    <JsGridExcelGridOpenButton entityName="Company" search={excelGridSearch}/>
                </div>
            </div>

            <div className="company-manage-panel__workspace">
                <div className="code-manage-panel__list code-manage-scope company-manage-panel__list">
                    <CompanyManageSidebarList
                        items={data}
                        selectedId={selectedId}
                        onSelect={handleSelectCompany}
                    />
                    <div className="company-manage-panel__list-footer">
                        <CommonPagination page={pageInfo} onPageChange={handlePageChange}/>
                        <span className="company-manage-panel__total">
                            total {pageInfo?.totalElements ?? 0}
                        </span>
                    </div>
                </div>

                <div className="code-manage-panel__editor code-manage-scope company-manage-panel__editor">
                    <div className="company-manage-editor__head">
                        <div className="company-manage-editor__title-block">
                            <h3 className="company-manage-editor__title">
                                {selectedCompany?.name ?? "업체 상세"}
                            </h3>
                            {selectedCompany?.number && (
                                <p className="company-manage-editor__subtitle">
                                    관리번호 {selectedCompany.number}
                                </p>
                            )}
                        </div>
                        {showEditor && (
                            <PanelTextNav
                                ariaLabel="업체 상세 탭"
                                options={COMPANY_TAB_OPTIONS}
                                value={activeTab}
                                onChange={setActiveTab}
                            />
                        )}
                    </div>

                    <div
                        className={`company-manage-editor__body${
                            showEditor && activeTab !== "info" ? " company-manage-editor__body--tab-list" : ""
                        }`}
                    >
                        {!showEditor ? (
                            <CompanyEditorEmpty
                                loading={loadingDetail}
                                hasSelection={!!data?.length}
                            />
                        ) : (
                            <>
                                {activeTab === "info" && (
                                    <CompanyManageEditor
                                        company={selectedCompany}
                                        setCompany={setSelectedCompany}
                                        onDeleted={handleDeletedCompany}
                                    />
                                )}
                                {activeTab === "related" && (
                                    <CompanyRelatedInfoPanel key={selectedCompany.id} id={selectedCompany.id}/>
                                )}
                                {activeTab === "history" && (
                                    <CommonHistoryList key={selectedCompany.id} id={selectedCompany.id} tableName="업체관리"/>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CompanyListType;
